import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';
import { useSelector, useDispatch } from 'react-redux';
import EditableCell from './EditableCell';
import {
  fetchUnifiedGroupData,
  fetchOrderStatuses,
  setLoadingMode,
  setTotalRecords,
  updateOrderStatus,
  addRowToTable,
} from '../redux/columnsSlice';
import { Button } from '../components/ui/button';
import { buildApiUrl } from '../utils/api';

const DataTable = ({ finalColumns, finalRows }) => {
  const getRowBackgroundColor = (status) => {
    const normalizedStatus = status?.startsWith('wc-') ? status : `wc-${status}`;
    switch (normalizedStatus) {
      case 'wc-pending':
        return '#FCF8E3';
      case 'wc-processing':
        return '#e3efe3';
      case 'wc-on-hold':
        return '#fbecd3';
      case 'wc-completed':
        return '#e2eaef';
      case 'wc-cancelled':
        return '#F2F2F2';
      case 'wc-refunded':
        return '#FCF8E3';
      case 'wc-failed':
        return '#F2DEDE';
      case 'wc-checkout-draft':
        return '#E2E3E5';
      default:
        return 'transparent';
    }
  };

  const table = useReactTable({
    data: finalRows,
    columns: finalColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    pageCount: 1,
    getRowId: (row, index) => {
      if (row.order_item_id) return `${row.order_id}-${row.order_item_id}`;
      if (row.product_id) return `${row.order_id}-${row.product_id}`;
      return `${row.order_id}-${index}`;
    },
  });

  return (
    <div className="autocrm-table-wrapper">
      <table className="autocrm-data-table">
        <thead className="autocrm-data-table-head">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="autocrm-table-row">
              {headerGroup.headers.map((header) => (
                <th key={header.id} className="autocrm-table-th">
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="autocrm-data-table-body">
          {table.getRowModel().rows.length === 0 ? (
            <tr className="autocrm-table-row">
              <td colSpan={finalColumns.length} className="autocrm-table-empty">
                No hay datos para mostrar
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => (
              <tr
                key={row.original?.order_id || row.id}
                className="autocrm-table-row"
                style={{
                  backgroundColor: row.original?.status
                    ? getRowBackgroundColor(row.original.status)
                    : 'inherit',
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="autocrm-table-td">
                    {cell.column.id === 'order_id' ? (
                      <a
                        href={`https://armstrongcontigo.mx/wp-admin/post.php?post=${row.original?.order_id}&action=edit`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#0073aa', textDecoration: 'underline' }}
                      >
                        {row.original?.order_id}
                      </a>
                    ) : (
                      flexRender(cell.column.columnDef.cell, cell.getContext())
                    )}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

const UnifiedTable = ({ unique_key }) => {
  const dispatch = useDispatch();
  const loadingMode = useSelector((state) => state.columns.loadingMode);
  const isModoBusqueda = useMemo(() => loadingMode === 'search', [loadingMode]);

  // ✅ 1. Declarar lastCheckedRef
  const lastCheckedRef = useRef(new Date().toISOString());

  // ✅ 2. Agregar useEffect para el polling (después de la carga inicial)
  useEffect(() => {
    const intervalId = setInterval(async () => {
      if (loadingMode !== 'date') {
        console.log(`⏸️ Polling detenido. Modo actual: ${loadingMode}`);
        return;
      }

      const since = lastCheckedRef.current;
      console.log(`🌀 Polling ejecutado (modo: ${loadingMode}) desde: ${since}`);

      try {
        const response = await fetch(
          `/wp-json/autocrm/v1/woocommerce_resource?resource_type=orders&since=${since}&limit=5`
        );
        const data = await response.json();

        if (data.success && data.data.length > 0) {
          console.log(`✅ Nuevos pedidos recibidos: ${data.data.length}`);
          lastCheckedRef.current = new Date().toISOString();
          for (const order of data.data) {
            console.log('📦 Insertando pedido al store:', order.order_id);
            dispatch(addRowToTable(order));
          }
        } else {
          console.log('🔍 No hay nuevos pedidos');
        }
      } catch (error) {
        console.error('❌ Error al hacer polling de pedidos:', error);
      }
    }, 60000); // 1 minuto

    return () => clearInterval(intervalId);
  }, [dispatch, loadingMode]);

  // Al montar, se cargan los estatus de pedidos.
  useEffect(() => {
    dispatch(fetchOrderStatuses()).unwrap().catch(() => {});
  }, [dispatch]);

  // Fechas por defecto: 14 días atrás y mañana.
  const defaultFechaInicio = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 14);
    return d.toISOString().substr(0, 10);
  }, []);
  const defaultFechaFin = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().substr(0, 10);
  }, []);

  // Estados para datos y carga.
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState('');
  const [recargaLabel, setRecargaLabel] = useState('Recargar');

  // Refs para inputs.
  const fechaInicioRef = useRef(null);
  const fechaFinRef = useRef(null);
  const searchTermRef = useRef(null);

  const updatedData = useSelector(
    (state) => state.columns.dataByunique_key[unique_key]?.rows
  );
  const columnsFromRedux = useSelector(
    (state) => state.columns.dataByunique_key[unique_key]?.columns || []
  );

  useEffect(() => {
    if (updatedData) {
      setTableData(updatedData);
    }
  }, [updatedData]);

  // Función para convertir los datos a CSV.
  const convertToCsv = (rows) => {
    const headers = finalColumnsForTable.map((col) => aliasMapping[col.column_name]);
    let csvContent = headers.join(',') + '\n';
    rows.forEach((row) => {
      const rowData = finalColumnsForTable.map((col) => {
        const cellValue = row[col.accessorKey || col.column_name];
        const safeValue = String(cellValue ?? '')
          .replace(/(\r\n|\n|\r)/gm, ' ')
          .replace(/,/g, ' ');
        return `"${safeValue}"`;
      });
      csvContent += rowData.join(',') + '\n';
    });
    return csvContent;
  };

  // Manejador para exportar CSV.
  const handleExportCSV = () => {
    const csv = '\uFEFF' + convertToCsv(tableData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'datos_export.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Función para cargar datos usando filtros.
  // Retornamos la promesa para poder encadenar .finally() en handleReset
  const loadDataWithFilters = useCallback(
    (
      fechaInicioValue,
      fechaFinValue,
      selectedColumnValue,
      searchTermValue,
      customPerPage = 999999
    ) => {
      setIsLoading(true);
      const mode = searchTermValue || selectedColumnValue ? 'search' : 'date';
      dispatch(setLoadingMode(mode));

      const columnFilter = selectedColumnValue === 'order_id' ? 'os.order_id' : selectedColumnValue;

      return dispatch(
        fetchUnifiedGroupData({
          unique_key,
          fechaInicio: fechaInicioValue,
          fechaFin: fechaFinValue,
          perPage: customPerPage,
          page: 1,
          p_column_filter: columnFilter,
          p_field_filter: searchTermValue,
        })
      )
        .unwrap()
        .then((result) => {
          setTableData(result.rows || []);
          dispatch(
            setTotalRecords({ unique_key, totalRecords: result.totalRecords || 0 })
          );
        })
        .catch(() => {})
        .finally(() => setIsLoading(false));
    },
    [dispatch, unique_key]
  );

  // Carga inicial: se solicitan solo 50 elementos.
  useEffect(() => {
    if (unique_key) {
      loadDataWithFilters(defaultFechaInicio, defaultFechaFin, '', '', 50);
    }
  }, [loadDataWithFilters, unique_key, defaultFechaInicio, defaultFechaFin]);

  // Manejadores para búsqueda y reseteo.
  const handleDateSearch = useCallback(() => {
    const newFechaInicio = fechaInicioRef.current.value;
    const newFechaFin = fechaFinRef.current.value;
    loadDataWithFilters(newFechaInicio, newFechaFin, '', '');
  }, [loadDataWithFilters]);

  const handleColumnSearch = useCallback(() => {
    const newFechaInicio = fechaInicioRef.current.value;
    const newFechaFin = fechaFinRef.current.value;
    const newSearchTerm = searchTermRef.current.value;
    loadDataWithFilters(newFechaInicio, newFechaFin, selectedColumn, newSearchTerm);
  }, [loadDataWithFilters, selectedColumn]);

  // ✅ 3. En handleReset, reiniciar también lastCheckedRef
  const handleReset = () => {
    if (fechaInicioRef.current) fechaInicioRef.current.value = defaultFechaInicio;
    if (fechaFinRef.current) fechaFinRef.current.value = defaultFechaFin;
    if (searchTermRef.current) searchTermRef.current.value = '';

    setSelectedColumn('');
    setRecargaLabel('Recargando...');
    lastCheckedRef.current = new Date().toISOString(); // 🔁 Reset de referencia

    return loadDataWithFilters(defaultFechaInicio, defaultFechaFin, '', '', 50)
      .finally(() => setRecargaLabel('Recargar'));
  };

  // Mapeo de alias para encabezados (para visualización).
  const aliasMapping = useMemo(() => {
    return columnsFromRedux.reduce((acc, col) => {
      acc[col.column_name] = col.alias || col.column_name;
      return acc;
    }, {});
  }, [columnsFromRedux]);

  // Orden y configuración de columnas.
  const finalColumnsForTable = useMemo(() => {
    const sortedCols = [...columnsFromRedux].sort(
      (a, b) => Number(a.order || 0) - Number(b.order || 0)
    );

    return sortedCols.map((col) => {
      const newCol = {
        ...col,
        accessorKey: col.column_name,
        header: col.alias || col.column_name,
        enableSorting: true,
        enableColumnFilter: true,
        options: col.options || [],
      };

      if (col.column_name === 'status') {
        newCol.cell = ({ getValue, row }) => {
          const value = getValue();
          const options = [
            { value: 'wc-pending', label: 'Pendiente' },
            { value: 'wc-processing', label: 'Procesando' },
            { value: 'wc-on-hold', label: 'En espera' },
            { value: 'wc-completed', label: 'Completado' },
            { value: 'wc-cancelled', label: 'Cancelado' },
            { value: 'wc-refunded', label: 'Reembolsado' },
            { value: 'wc-failed', label: 'Fallido' },
            { value: 'wc-checkout-draft', label: 'Borrador' },
          ];

          const handleChange = (e) => {
            const newStatus = e.target.value;
            dispatch(
              updateOrderStatus({
                unique_key,
                order_id: row.original.order_id,
                new_status: newStatus,
              })
            )
              .unwrap()
              .then(() =>
                console.log(
                  `Estado actualizado a ${newStatus} para el pedido ${row.original.order_id}`
                )
              )
              .catch((error) => console.error('Error actualizando el estado:', error));
          };

          return (
            <select value={value} onChange={handleChange}>
              {options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          );
        };
      } else if (col.is_editable === true || col.is_editable === '1') {
        newCol.cell = (info) => (
          <EditableCell
            info={info}
            columnConfig={col}
            unique_key={unique_key}
            aliasMapping={aliasMapping}
          />
        );
      } else if (col.column_name === 'order_id') {
        newCol.cell = ({ getValue }) => (
          <a
            href={`https://armstrongcontigo.mx/wp-admin/post.php?post=${getValue()}&action=edit`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#0073aa', textDecoration: 'underline' }}
          >
            {getValue()}
          </a>
        );
      } else {
        newCol.cell = (info) => {
          const value = info.getValue();
          return typeof value === 'string' ? value : value;
        };
      }
      return newCol;
    });
  }, [columnsFromRedux, aliasMapping, unique_key, dispatch]);

  const finalRows = useMemo(() => (Array.isArray(tableData) ? tableData : []), [tableData]);

  // Opciones para el dropdown de búsqueda en la columna "status".
  const statusOptions = [
    { value: 'wc-pending', label: 'Pendiente' },
    { value: 'wc-processing', label: 'Procesando' },
    { value: 'wc-on-hold', label: 'En espera' },
    { value: 'wc-completed', label: 'Completado' },
    { value: 'wc-cancelled', label: 'Cancelado' },
    { value: 'wc-refunded', label: 'Reembolsado' },
    { value: 'wc-failed', label: 'Fallido' },
    { value: 'wc-checkout-draft', label: 'Borrador' },
  ];

  return (
    <div className="autocrm-dashboard-container">
      <div className="autocrm-toolbox autocrm-toolbar">
        <div className="autocrm-toolbar-section">
          <label className="autocrm-label" style={{ marginRight: '0.5rem' }}>
            Fecha inicio:
            <input
              type="date"
              ref={fechaInicioRef}
              defaultValue={defaultFechaInicio}
              className="autocrm-input"
              style={{ marginLeft: '0.3rem' }}
            />
          </label>
          <label className="autocrm-label" style={{ marginLeft: '1rem', marginRight: '0.5rem' }}>
            Fecha fin:
            <input
              type="date"
              ref={fechaFinRef}
              defaultValue={defaultFechaFin}
              className="autocrm-input"
              style={{ marginLeft: '0.3rem' }}
            />
          </label>
          <Button onClick={handleDateSearch} className="autocrm-btn autocrm-btn-primary" style={{ marginLeft: '1rem' }}>
            Consultar
          </Button>
        </div>
        <div className="autocrm-toolbar-section">
          <select value={selectedColumn} onChange={(e) => setSelectedColumn(e.target.value)} style={{ marginRight: '0.5rem' }}>
            <option value="">Seleccione una columna</option>
            {columnsFromRedux.map((col) => (
              <option key={col.column_name} value={col.column_name}>
                {col.alias || col.column_name}
              </option>
            ))}
          </select>
          {selectedColumn === 'status' ? (
            <select ref={searchTermRef} defaultValue="" style={{ marginRight: '0.5rem' }}>
              <option value="">Seleccione un estado</option>
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          ) : selectedColumn === 'order_date' ? (
            <input type="date" ref={searchTermRef} defaultValue="" style={{ marginRight: '0.5rem' }} />
          ) : (
            <input type="text" ref={searchTermRef} placeholder="Buscar..." defaultValue="" style={{ marginRight: '0.5rem' }} />
          )}
          <Button onClick={handleColumnSearch} className="autocrm-btn autocrm-btn-primary">
            Buscar
          </Button>
        </div>
        <div className="autocrm-toolbar-section" style={{ textAlign: 'right' }}>
          <Button onClick={handleExportCSV} className="autocrm-btn autocrm-btn-primary autocrm-btn-export-csv">
            Exportar CSV
          </Button>
          <Button
            title={isModoBusqueda ? 'Modo búsqueda activo, polling detenido' : 'Polling activo'}
            onClick={handleReset}
            className={`autocrm-btn autocrm-btn-primary ${isModoBusqueda ? 'autocrm-btn-danger' : ''}`}
            style={{ marginLeft: '0.5rem' }}
            disabled={recargaLabel === 'Recargando...'}
          >
            {recargaLabel}
          </Button>
        </div>
      </div>
      {isLoading ? (
        <p className="autocrm-loading">Cargando datos unificados...</p>
      ) : finalColumnsForTable.length === 0 ? (
        <p className="autocrm-info">No hay columnas configuradas para mostrar.</p>
      ) : finalRows.length === 0 ? (
        <p className="autocrm-info">No hay filas para mostrar.</p>
      ) : (
        <DataTable finalColumns={finalColumnsForTable} finalRows={finalRows} />
      )}
    </div>
  );
};

export default React.memo(UnifiedTable);
